import { Grid } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useEffect, useState } from 'react';

import ButtonComponent from '#app/Component/ButtonComponent';
import DatePickerComponent from '#app/Component/DatePickerComponent';
import MultipleSelectComponent from '#app/Component/MultipleSelectComponent';
import ShadowBoxComponent from '#app/Component/ShadowBoxComponent';
import { Form, initFormHandler } from '#app/Handler/FormHandler';
import { generateApiPath } from '#app/Handler/api';
import { Filters } from '#app/View/Statistic/Order/StatisticOrder';
import OrderDocumentEntryPoints from '#app/entrypoints/orderDocument';
import useShops from '#app/hooks/useShops';
import { Channel, ChannelSelects } from '#app/typings/channel';
import { OrderDocumentType, OrderDocumentTypeTranslation } from '#app/typings/orderDocument';
import { PaymentMethod, PaymentMethodTranslation } from '#app/typings/orderPayment';

type Props = {
  handleSearch: (data: Filters) => void;
  filters: Filters;
};

const Search: React.FC<Props> = ({ handleSearch, filters }) => {
  const { shops } = useShops();
  const classes = useStyles();
  const [loadingExport, setLoadingExport] = useState<boolean>(false);
  const [withShops, setWithShops] = useState<boolean>(false);

  const [formSearch, setFormSearch] = useState<Form>({
    startDate: {
      name: 'startDate',
      label: 'Date de début',
      textHelper: 'Rechercher par date de début.',
      type: 'date',
      options: { validation: ['date'] },
      defaultValue: filters.startDate,
    },
    endDate: {
      name: 'endDate',
      label: 'Date de fin',
      textHelper: 'Rechercher par date de fin.',
      type: 'date',
      options: { validation: ['date'] },
      defaultValue: filters.endDate,
    },
    types: {
      name: 'types',
      label: 'Type',
      textHelper: 'Rechercher par type.',
      type: 'array',
      defaultValue: filters?.types,
    },
    channels: {
      name: 'channels',
      label: 'Canal',
      textHelper: 'Rechercher par canaux.',
      type: 'array',
      defaultValue: filters?.channels,
    },
    shops: {
      name: 'shops',
      label: 'Boutique',
      textHelper: 'Rechercher par boutiques.',
      type: 'array',
      defaultValue: filters?.shops,
    },
    paymentMethods: {
      name: 'paymentMethods',
      label: 'Methode de paiement',
      textHelper: 'Rechercher par methode de paiement.',
      type: 'array',
      defaultValue: filters?.paymentMethods,
    },
  });

  const handlerFormSearch = initFormHandler(formSearch, setFormSearch);
  useEffect(() => {
    handlerFormSearch.start();
  }, []);

  const getData = (): Filters => {
    const data = handlerFormSearch.getData();

    const paymentMethods: PaymentMethod[] = [];
    data.paymentMethods.forEach((paymentMethod: string) => {
      Object.entries(PaymentMethodTranslation).forEach(([key, value]) => {
        if (value === paymentMethod) {
          paymentMethods.push(key as PaymentMethod);
        }
      });
    });

    return {
      startDate: data.startDate,
      endDate: data.endDate,
      channels: data.channels,
      types: data.types,
      shops: data.shops,
      paymentMethods,
    };
  };

  const download = () => {
    setLoadingExport(true);

    const aElement = document.createElement('a');
    aElement.href = generateApiPath(OrderDocumentEntryPoints.getStatisticDownload, {}, getData());
    document.body.appendChild(aElement);
    aElement.click();
    document.body.removeChild(aElement);

    setTimeout(() => setLoadingExport(false), 10000);
  };

  return (
    <ShadowBoxComponent>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={6} lg={3}>
          <DatePickerComponent id="startDate" handler={handlerFormSearch} />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <DatePickerComponent id="endDate" handler={handlerFormSearch} />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <MultipleSelectComponent
            withSelectAll
            id="channels"
            options={ChannelSelects}
            handler={handlerFormSearch}
            onChange={(value) => {
              const withShops = (value as unknown as string[]).includes(Channel.BOUTIQUES.toString());
              setWithShops(withShops);
              if (!withShops) {
                handlerFormSearch.setValue('shops', []);
              }
            }}
          />
        </Grid>
        {withShops && (
          <Grid item xs={12} sm={6} lg={3}>
            <MultipleSelectComponent id="shops" options={shops} handler={handlerFormSearch} />
          </Grid>
        )}
        <Grid item xs={12} sm={6} lg={3}>
          <MultipleSelectComponent
            id="types"
            options={(Object.keys(OrderDocumentTypeTranslation) as OrderDocumentType[])
              .filter((orderType) => orderType !== OrderDocumentType.DELIVERY_NOTE_ORDER_CLIENT)
              .map((orderType) => ({
                value: orderType,
                label: OrderDocumentTypeTranslation[orderType],
              }))}
            handler={handlerFormSearch}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <MultipleSelectComponent
            id="paymentMethods"
            options={Array.from(new Set(Object.values(PaymentMethodTranslation))).map((paymentMethod) => ({
              value: paymentMethod,
              label: paymentMethod,
            }))}
            handler={handlerFormSearch}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <ButtonComponent className={classes.button} label="Rechercher" onClick={() => handleSearch(getData())} />
          <ButtonComponent
            className={classes.button}
            label="Telecharger"
            onClick={() => download()}
            disabled={loadingExport}
            loading={loadingExport}
          />
        </Grid>
      </Grid>
    </ShadowBoxComponent>
  );
};

const useStyles = makeStyles({
  button: {
    margin: '6px !important',
  },
});

export default Search;
